<template>
  <Pane
    :value="documentsVersioningPane.isOpen"
    temporary
    :tabs="tabs"
    v-model:current-tab="tab"
    data-locator="documents-versioning-pane"
    @update:modelValue="onClosePane"
  >
    <template #content-manage>
      <DocumentVersioning
        :loading="postVersionFromImanagePending || versionsLoading"
        :selected-documents="documentsVersioningPane.documents"
        @add-version="onAddVersion"
        @add-version-from-imanage="onAddVersionFromImanage"
        @close-pane="onClosePane"
      />
    </template>
    <template #content-history>
      <DocumentVersioningHistory :selected-documents="documentsVersioningPane.documents" @share-version="onShareVersion" />
    </template>
  </Pane>
</template>

<script>
import { defineComponent } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import DocumentVersioning from '@/common/document-versioning/DocumentVersioning.vue'
import DocumentVersioningHistory from '@/common/document-versioning/DocumentVersioningHistory.vue'
import Pane from '@/common/Pane.vue'
import { toast } from '@/common/utils/toast'
import {
  ADD_VERSION,
  CHANGE_DOCUMENTS_VERSIONING_PANE_TAB,
  CLOSE_DOCUMENTS_VERSIONING_PANE,
  DESELECT_ALL_DOCUMENTS,
  GET_PAGINATED_DOCUMENTS,
  POST_VERSION_FROM_IMANAGE,
  SET_DOCUMENTS_VERSIONING_PANE,
  SET_TABLE_DOCUMENTS_PANE,
} from '@/store/modules/documents/action_types'

const TRANSLATION_KEY_PREFIX = 'project.documents.pane.DocumentsVersioningPane'

export default defineComponent({
  name: 'DocumentsVersioningPane',
  components: {
    DocumentVersioning,
    DocumentVersioningHistory,
    Pane,
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapGetters('room', ['isCurrentUserPm', 'currentUserRights']),
    ...mapState('documents', ['documentsVersioningPane', 'numberingEnabled', 'postVersionFromImanagePending', 'versionsLoading']),
    canManageVersions () {
      return this.isCurrentUserPm || this.currentUserRights.canUploadVersions
    },
    tab: {
      get () {
        return this.documentsVersioningPane.tab
      },
      set (val) {
        this.CHANGE_DOCUMENTS_VERSIONING_PANE_TAB(val)
      },
    },
    tabs () {
      const tabs = []
      if (this.documentsVersioningPane.documents.length === 1 && this.documentsVersioningPane.documents[0].type === 'file') {
        if (this.canManageVersions) {
          tabs.push({
            title: this.$t(`${TRANSLATION_KEY_PREFIX}.tabManage`),
            id: 'manage',
          })
        }
        if (this.canManageVersions || this.documentsVersioningPane.documents[0].nbVersions > 1) {
          tabs.push({
            title: this.canManageVersions ? this.$t(`${TRANSLATION_KEY_PREFIX}.tabHistory`) : this.$t(`${TRANSLATION_KEY_PREFIX}.tabHistoryGuests`),
            id: 'history',
          })
        }
      }
      return tabs
    },
  },
  methods: {
    ...mapActions('documents', [
      ADD_VERSION,
      CHANGE_DOCUMENTS_VERSIONING_PANE_TAB,
      CLOSE_DOCUMENTS_VERSIONING_PANE,
      DESELECT_ALL_DOCUMENTS,
      GET_PAGINATED_DOCUMENTS,
      POST_VERSION_FROM_IMANAGE,
      SET_DOCUMENTS_VERSIONING_PANE,
      SET_TABLE_DOCUMENTS_PANE,
    ]),
    async onAddVersion (data) {
      try {
        await this.ADD_VERSION(data)

        toast.success(this.$t(`${TRANSLATION_KEY_PREFIX}.addVersionFromClosdSuccess`))

        this.GET_PAGINATED_DOCUMENTS()

        this.onClosePane()
      } catch (error) {
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.addVersionFromClosdError`))
      }
    },
    async onAddVersionFromImanage (data) {
      try {
        await this.POST_VERSION_FROM_IMANAGE({
          mnemo: this.mnemo,
          data: data,
        })

        toast.success(this.$t(`${TRANSLATION_KEY_PREFIX}.addVersionFromImanageSuccess`))

        this.GET_PAGINATED_DOCUMENTS()

        this.onClosePane()
      } catch (error) {
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.addVersionFromImanageError`))
      }
    },
    onClosePane (value) {
      if (!value) {
        this.CLOSE_DOCUMENTS_VERSIONING_PANE()
      }
    },
    onShareVersion (data) {
      // we have to use destructuration to break reference to the variable in the store
      const currentDocument = { ...this.documentsVersioningPane.documents[0] }

      this.CLOSE_DOCUMENTS_VERSIONING_PANE()
      this.SET_TABLE_DOCUMENTS_PANE({
        isOpen: true,
        documents: [data],
        tab: 'share',
        forcePmSelection: true,
        previousDocument: this.documentsVersioningPane.documents[0],
        onCloseCallback: () => {
          this.SET_DOCUMENTS_VERSIONING_PANE({
            isOpen: true,
            documents: [currentDocument],
            tab: 'history',
          })
        },
      })
    },
  },
})
</script>
<style lang="scss" scoped>
@media (min-width:600px) {
  .v-navigation-drawer {
    min-width: 480px !important;
  }
}
</style>
